import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Menu, Phone, Clock, MapPin } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_10d419fc-5e2e-447b-b30b-f3a6e5d925a0/artifacts/qt2o5wzu_file_0000000029f471f48789e22e477ba51d.png";

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/menu", label: "Menu" },
  { href: "/about", label: "About" },
  { href: "/contact", label: "Contact" },
];

const Navbar = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-card/95 backdrop-blur-sm border-b border-border">
      {/* Top bar with contact info */}
      <div className="hidden md:block bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 py-2 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Open Daily 8am – 12pm
            </span>
            <span className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              59 Torquay Road, Paignton
            </span>
          </div>
          <a 
            href="tel:+447424864732" 
            className="flex items-center gap-2 hover:text-wheat transition-colors"
          >
            <Phone className="h-4 w-4" />
            07424 864732
          </a>
        </div>
      </div>

      {/* Main navbar */}
      <div className="container mx-auto px-4">
        <nav className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
            <img 
              src={LOGO_URL} 
              alt="Bacon & Brews" 
              className="h-16 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`font-body font-medium text-base transition-colors hover:text-accent ${
                  isActive(link.href)
                    ? "text-accent nav-link-active"
                    : "text-foreground"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Order Now Button (Desktop) */}
          <div className="hidden md:flex items-center gap-4">
            <Button asChild variant="hero" size="lg">
              <Link to="/order">Order Now</Link>
            </Button>
          </div>

          {/* Mobile Menu */}
          <div className="flex md:hidden items-center gap-2">
            <Button asChild variant="brand" size="sm">
              <Link to="/order">Order</Link>
            </Button>
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-foreground">
                  <Menu className="h-6 w-6" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] bg-card">
                <SheetTitle className="font-display text-xl text-foreground">
                  Menu
                </SheetTitle>
                <div className="flex flex-col gap-6 mt-8">
                  {navLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setIsOpen(false)}
                      className={`font-body font-medium text-lg transition-colors ${
                        isActive(link.href)
                          ? "text-accent"
                          : "text-foreground hover:text-accent"
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <div className="border-t border-border pt-6 mt-2 space-y-4">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      <span className="text-sm">8am – 12pm Daily</span>
                    </div>
                    <a 
                      href="tel:+447424864732"
                      className="flex items-center gap-2 text-accent font-medium"
                    >
                      <Phone className="h-4 w-4" />
                      07424 864732
                    </a>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
